export function reviewParams(config, state) {
  const sorts = {latest: ['created_at', 'desc'], oldest: ['created_at', 'asc'], highest: ['rating', 'desc'], lowest: ['rating', 'asc'], helpful: ['most_helpful', 'desc']};
  const [by, direction] = sorts[state.sort] || sorts.latest;
  const params = new URLSearchParams({shop_domain: config.shop, api_token: config.token, handle: config.handle, json_request: 'true', per_page: '6', page: String(state.page || 1), sort_by: state.media ? 'with_pictures' : by});
  if (!state.media) params.set('sort_dir', direction);
  if (state.rating) params.set('filter_rating', state.rating);
  if (state.query?.trim()) params.set('search', state.query.trim());
  return params;
}

function photoUrl(value) {
  const candidate = typeof value === 'string' ? value : value?.original || value?.huge || value?.big || value?.url || value?.small;
  try { const url = new URL(candidate); return url.protocol === 'https:' ? url.href : null; } catch { return null; }
}

export function relativeDate(iso, locale) {
  const seconds = (Date.parse(iso) - Date.now()) / 1000, format = new Intl.RelativeTimeFormat(locale, {numeric: 'auto'});
  for (const [unit, size] of [['year', 31536000], ['month', 2592000], ['week', 604800], ['day', 86400], ['hour', 3600], ['minute', 60]]) {
    if (Math.abs(seconds) >= size) return format.format(Math.round(seconds / size), unit);
  }
  return format.format(0, 'minute');
}

export function normalizeReview(review) {
  return {
    id: String(review.uuid || ''), rating: Math.max(1, Math.min(5, Number(review.rating) || 1)),
    title: String(review.title || ''), body: String(review.body || ''), author: String(review.reviewer_name || review.reviewer_initial || ''),
    verified: review.verified_buyer === true, date: review.created_at, pinned: review.pinned === true,
    variant: String(review.product_variant_title || ''), reply: String(review.reply_content || ''),
    photos: (review.pictures_urls || []).map(photoUrl).filter(Boolean),
    // "Rekommenderar" (Isac 2026-10-19): shown on 4-5 star reviews; an explicit recommend answer from a Judge.me form question wins
    recommend: (() => {
      const answer = (review.cf_answers || []).find(item => /rekommend|recommend/i.test(String(item.cf_question_title || item.question || '')));
      if (answer) return /^(ja|yes|true|1)$/i.test(String(answer.value ?? answer.answer ?? '').trim());
      return Number(review.rating) >= 4;
    })(),
    product: String(review.product_title || ''), productUrl: /^\/products\/[\w-]+$/.test(review.product_url || '') ? review.product_url : ''
  };
}

// Worker meta (flavour + explicit recommend answer) keyed by Judge.me uuid; a row exists only for a purchase our worker verified, so it also marks the review verified. No row keeps the Judge.me-only behaviour
export function mergeMeta(review, meta) {
  const row = meta && meta.get(review.id);
  if (!row) return review;
  return {...review, verified: true, flavour: String(row.variantTitle || ''), recommend: typeof row.recommend === 'boolean' ? row.recommend : review.recommend};
}

export function metaMap(data) {
  const map = new Map();
  for (const row of (data && Array.isArray(data.reviews) ? data.reviews : [])) if (row && row.judgemeUuid) map.set(String(row.judgemeUuid), row);
  return map;
}

if (typeof customElements !== 'undefined' && !customElements.get('pl-product-reviews')) {
  customElements.define('pl-product-reviews', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.config = JSON.parse(this.querySelector('[data-review-config]').textContent);
      this.labels = this.config.labels;
      this.state = {page: 1, rating: '', query: '', sort: 'latest', media: false};
      this.rows = [];
      this.meta = new Map();
      this.photos = this.config.photos || [];
      this.$ = selector => this.querySelector(selector);
      this.form = this.$('[data-filters]');
      this.list = this.$('[data-review-list]');
      this.status = this.$('[data-review-status]');
      this.more = this.$('[data-review-more]');
      this.modal = this.$('dialog');
      this.addEventListener('click', event => this.onClick(event));
      this.form.addEventListener('submit', event => { event.preventDefault(); this.readFilters(); });
      this.form.addEventListener('change', event => { if (event.target.type !== 'search') this.readFilters(); });
      this.modal.addEventListener('close', () => { document.body.classList.remove('pl-review-media-open'); this.returnFocus?.focus(); });
      this.modal.addEventListener('click', event => { if (event.target === this.modal) this.modal.close(); });
      this.renderGallery();
      this.adoptSummary();
      const start = () => {
        if (this.started) return;
        this.started = true;
        this.load();
        this.loadPhotos();
        this.refreshAggregate();
        this.loadMeta();
      };
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => { if (entries.some(e => e.isIntersecting)) { this.observer.disconnect(); start(); } }, {rootMargin: '400px'});
        this.observer.observe(this);
      } else start();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.request?.abort();
      clearInterval(this.summaryPoll);
      if (this.modal?.open) this.modal.close();
    }

    element(tag, className, text) {
      const el = document.createElement(tag);
      if (className) el.className = className;
      if (text != null) el.textContent = text;
      return el;
    }

    stars(rating) {
      const el = this.element('span', 'pl-pr__stars', '★★★★★');
      el.setAttribute('role', 'img');
      el.setAttribute('aria-label', `${rating} / 5`);
      el.style.setProperty('--fill', `${rating / 5 * 100}%`);
      return el;
    }

    adoptSummary() {
      let tries = 0;
      const read = () => {
        const widget = document.querySelector('.jdgm-rev-widg');
        if (!widget) return ++tries >= 40;
        const rating = Number(widget.dataset.averageRating), count = Number(widget.dataset.numberOfReviews);
        if (rating > 0 && count >= 0) this.aggregate(rating, count);
        const histogram = widget.querySelectorAll('.jdgm-histogram__row');
        const host = this.$('[data-histogram]');
        if (histogram.length && !host.children.length) histogram.forEach(row => {
          const number = row.dataset.rating, frequency = Number(row.dataset.frequency) || 0;
          if (!/^[1-5]$/.test(number)) return;
          const button = this.element('button', 'pl-pr__histogram-row');
          button.type = 'button'; button.dataset.filterRating = number;
          button.setAttribute('aria-label', `${number} ${this.labels.stars}, ${frequency} ${this.labels.reviews}`);
          button.append(this.element('span', '', number));
          const track = this.element('span', 'pl-pr__track'), fill = this.element('span');
          fill.style.width = `${count ? frequency / count * 100 : 0}%`; track.append(fill);
          button.append(track); host.append(button);
        });
        // Preserve the provider's widget as the aggregate source, without its old curated accordion.
        const native = widget.closest('.jdgm-review-widget') || widget;
        const source = this.$('[data-native-source]');
        if (!source.contains(native)) {
          const original = native.closest('.shopify-app-block');
          source.append(native);
          if (original) original.hidden = true;
        }
        return true;
      };
      if (!read()) this.summaryPoll = setInterval(() => { if (read()) clearInterval(this.summaryPoll); }, 500);
    }

    aggregate(rating, count) {
      this.$('[data-review-average]').textContent = rating.toFixed(1);
      this.$('[data-review-total]').textContent = `${count.toLocaleString(this.config.locale)} ${this.labels.reviews}`;
      this.$('[data-overall-stars]').replaceChildren(this.stars(rating));
    }

    async refreshAggregate() {
      try {
        const params = new URLSearchParams({shop_domain: this.config.shop, api_token: this.config.token, handle: this.config.handle});
        const response = await fetch(`https://judge.me/api/v1/widgets/preview_badge?${params}`);
        if (!response.ok) return;
        const data = await response.json();
        const doc = new DOMParser().parseFromString(data.badge || '', 'text/html');
        const badge = doc.querySelector('[data-average-rating]');
        const rating = Number(badge?.dataset.averageRating), count = Number(badge?.dataset.numberOfReviews);
        if (rating > 0 && count >= 0 && this.isConnected) this.aggregate(rating, count);
      } catch { /* Keep the rendered last-known aggregate if the provider is temporarily unavailable. */ }
    }

    readFilters() {
      const fields = new FormData(this.form);
      this.state = {page: 1, query: String(fields.get('query') || ''), rating: String(fields.get('rating') || ''), sort: this.form.elements.sort.value, media: fields.has('media')};
      this.form.elements.sort.disabled = this.state.media;
      this.load();
    }

    async load(append = false) {
      this.request?.abort();
      const controller = new AbortController(); this.request = controller;
      this.setAttribute('aria-busy', 'true');
      this.status.textContent = this.labels.loading;
      // keep the button in place while appending (focus and scroll stay put); only a fresh query hides it
      // aria-disabled, not disabled: a disabled button drops keyboard focus
      this.more.setAttribute('aria-disabled', 'true');
      if (append) { this.more.dataset.label ??= this.more.textContent; this.more.textContent = this.labels.loading; }
      else this.more.hidden = true;
      if (!append) this.list.hidden = true;
      this.$('[data-review-retry]').hidden = true;
      this.$('[data-review-reset]').hidden = !(this.state.rating || this.state.query || this.state.media || this.state.sort !== 'latest');
      try {
        const params = reviewParams(this.config, this.state);
        const response = await fetch(`https://judge.me/api/v1/widgets/product_review?${params}`, {signal: controller.signal});
        if (!response.ok) throw new Error('Review provider unavailable');
        const data = await response.json();
        if (!Array.isArray(data.reviews)) throw new Error('Invalid review response');
        if (this.request !== controller || !this.isConnected) return;
        const incoming = data.reviews.map(normalizeReview);
        this.rows = append ? [...new Map([...this.rows, ...incoming].map(row => [row.id, row])).values()] : incoming;
        this.renderRows();
        this.list.hidden = false;
        this.pages = Number(data.total_pages) || 0;
        this.more.hidden = this.state.page >= this.pages;
        this.status.textContent = this.rows.length ? '' : this.labels.empty;
        this.$('[data-review-empty]').hidden = this.rows.length > 0;
      } catch (error) {
        if (error.name === 'AbortError' || this.request !== controller) return;
        this.status.textContent = this.labels.error;
        this.$('[data-review-retry]').hidden = false;
      } finally {
        if (this.request === controller) { this.removeAttribute('aria-busy'); this.more.removeAttribute('aria-disabled'); if (this.more.dataset.label) this.more.textContent = this.more.dataset.label; }
      }
    }

    renderRows() {
      const fragment = document.createDocumentFragment();
      // Judge.me always puts its pinned review first on page 1; for date sorts, put it back in date order
      const byDate = this.state.sort === 'latest' || this.state.sort === 'oldest';
      let rows = byDate ? [...this.rows].sort((x, y) => (Date.parse(y.date) || 0) - (Date.parse(x.date) || 0)) : this.rows;
      if (byDate) {
        // the pinned review is injected on page 1 whatever its date; drop it while it falls outside the loaded date range
        const times = this.rows.filter(row => !row.pinned).map(row => Date.parse(row.date)).filter(Boolean);
        const lo = Math.min(...times), hi = Math.max(...times);
        rows = rows.filter(row => !row.pinned || (Date.parse(row.date) >= lo && Date.parse(row.date) <= hi));
      }
      if (byDate && this.state.sort === 'oldest') rows.reverse();
      rows.map(row => mergeMeta(row, this.meta)).forEach(review => {
        const article = this.element('article', 'pl-pr__review'); article.dataset.reviewId = review.id;
        const person = this.element('div', 'pl-pr__person');
        const initials = (review.author || '').trim().split(/\s+/).filter(Boolean).slice(0, 2).map(part => part[0]).join('').toUpperCase();
        const avatar = this.element('span', 'pl-pr__avatar', initials); avatar.setAttribute('aria-hidden', 'true');
        const identity = this.element('div'); identity.append(this.element('strong', '', review.author || this.labels.customer));
        if (review.verified) {
          const badge = this.element('span', 'pl-pr__verified');
          badge.innerHTML = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 0l2 1.5 2.5-.1.8 2.4 2 1.5-.8 2.4.8 2.4-2 1.5-.8 2.4-2.5-.1L8 16l-2-1.5-2.5.1-.8-2.4-2-1.5.8-2.4-.8-2.4 2-1.5.8-2.4 2.5.1z" fill="currentColor"/><path d="M4.8 8.2l2.1 2.1 4.3-4.4" stroke="#141414" stroke-width="1.6" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>';
          badge.append(this.labels.verified); identity.append(badge);
        }
        person.append(avatar, identity);
        const content = this.element('div', 'pl-pr__content');
        const meta = this.element('div', 'pl-pr__review-meta'); meta.append(this.stars(review.rating));
        if (review.date && !isNaN(Date.parse(review.date))) {
          const date = this.element('time', '', relativeDate(review.date, this.config.locale));
          date.dateTime = review.date; date.title = new Intl.DateTimeFormat(this.config.locale, {dateStyle: 'long'}).format(new Date(review.date)); meta.append(date);
        }
        content.append(meta);
        if (review.pinned && !byDate) content.append(this.element('span', 'pl-pr__pinned', this.labels.featured));
        if (review.title) content.append(this.element('h3', '', review.title));
        if (review.body) content.append(this.element('p', 'pl-pr__body', review.body));
        else content.append(this.element('p', 'pl-pr__body pl-pr__muted', this.labels.noComment));
        if (review.photos.length) {
          const photos = this.element('div', 'pl-pr__inline-photos');
          review.photos.forEach(url => {
            const index = this.ensurePhoto({url, review}); photos.append(this.photoButton(this.photos[index], index));
          }); content.append(photos);
        }
        const foot = this.element('div', 'pl-pr__foot');
        if (review.product) {
          const line = this.element('p', 'pl-pr__variant', `${this.labels.reviewFor} `);
          // reviews of this page's product get the full PDP name (™, subtitle, servings) from the section config
          const own = this.config.productLabel && review.productUrl && review.productUrl === this.config.productUrl;
          const name = own ? (review.flavour ? `${this.config.productLabel} / ${review.flavour}` : this.config.productLabel) : (review.variant ? `${review.product} / ${review.variant}` : review.product);
          if (review.productUrl) { const link = this.element('a', '', name); link.href = review.productUrl; line.append(link); } else line.append(name);
          foot.append(line);
        }
        if (review.recommend) {
          const rec = this.element('span', 'pl-pr__recommend');
          rec.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/></svg>';
          rec.append(this.labels.recommend); foot.append(rec);
        }
        if (foot.childElementCount) content.append(foot);
        if (review.reply) {
          const reply = this.element('div', 'pl-pr__reply');
          reply.append(this.element('strong', '', this.labels.replyFrom), this.element('p', '', review.reply));
          content.append(reply);
        }
        article.append(person, content); fragment.append(article);
      });
      this.list.replaceChildren(fragment);
    }

    ensurePhoto(photo) {
      let index = this.photos.findIndex(item => item.url === photo.url);
      if (index < 0) { this.photos.push(photo); index = this.photos.length - 1; }
      return index;
    }

    async loadMeta() {
      if (!this.config.productId) return;
      try {
        const response = await fetch(`/apps/loyalty/reviews/meta?product=${encodeURIComponent(this.config.productId)}`, {headers: {Accept: 'application/json'}});
        if (!response.ok) return;
        const meta = metaMap(await response.json());
        if (!this.isConnected || !meta.size) return;
        this.meta = meta;
        if (this.rows.length) this.renderRows();
      } catch { /* flavour and recommend are extras; the Judge.me review stands without them */ }
    }

    async loadPhotos() {
      try {
        const params = reviewParams(this.config, {page: 1, media: true}); params.set('per_page', '12');
        const response = await fetch(`https://judge.me/api/v1/widgets/product_review?${params}`);
        if (!response.ok) return;
        const data = await response.json();
        if (!this.isConnected) return;
        (data.reviews || []).map(normalizeReview).forEach(review => review.photos.forEach(url => this.ensurePhoto({url, review})));
        this.renderGallery();
      } catch { /* Approved customer photos remain available if the provider photo request fails. */ }
    }

    photoButton(photo, index) {
      const button = this.element('button', 'pl-pr__photo'); button.type = 'button'; button.dataset.photoIndex = index;
      button.setAttribute('aria-label', `${this.labels.openPhoto} ${index + 1}`);
      const image = this.element('img'); image.src = photo.url; image.alt = photo.caption || this.labels.customerPhoto;
      image.loading = 'lazy'; image.width = 140; image.height = 140; button.append(image); return button;
    }

    renderGallery() {
      this.$('[data-photo-strip]').replaceChildren(...this.photos.map((photo, index) => this.photoButton(photo, index)));
      this.$('[data-photo-empty]').hidden = true;
      this.$('[data-photo-heading]').hidden = this.photos.length === 0;
      this.$('[data-photo-controls]').hidden = this.photos.length < 2;
    }

    showPhoto(index) {
      this.photoIndex = (index + this.photos.length) % this.photos.length;
      const photo = this.photos[this.photoIndex]; if (!photo) return;
      this.$('[data-modal-image]').src = photo.url;
      this.$('[data-modal-image]').alt = photo.caption || this.labels.customerPhoto;
      const caption = this.$('[data-modal-caption]'); caption.replaceChildren();
      if (photo.review) {
        caption.append(this.stars(photo.review.rating), this.element('h3', '', photo.review.author));
        if (photo.review.title) caption.append(this.element('strong', 'pl-pr__dialog-title', photo.review.title));
        caption.append(this.element('p', '', photo.review.body));
        if (photo.review.verified) caption.append(this.element('span', 'pl-pr__verified', `✓ ${this.labels.verified}`));
      } else {
        if (photo.author) caption.append(this.element('h3', '', photo.author));
        if (photo.caption) caption.append(this.element('p', '', photo.caption));
      }
      this.$('[data-photo-position]').textContent = `${this.photoIndex + 1} / ${this.photos.length}`;
      this.modal.querySelectorAll('[data-photo-step]').forEach(button => button.hidden = this.photos.length < 2);
      if (!this.modal.open) { this.modal.showModal(); document.body.classList.add('pl-review-media-open'); }
    }

    onClick(event) {
      const button = event.target.closest('button'); if (!button || !this.contains(button)) return;
      if (button.hasAttribute('data-write-review')) document.dispatchEvent(new CustomEvent('pl:write-review', {detail: {trigger: button, productId: this.config.productId}}));
      if (button.hasAttribute('data-filter-rating')) { this.form.elements.rating.value = button.dataset.filterRating; this.readFilters(); }
      if (button.hasAttribute('data-review-reset')) { this.form.reset(); this.readFilters(); }
      if (button.hasAttribute('data-review-retry')) this.load(this.state.page > 1);
      if (button.hasAttribute('data-review-more') && button.getAttribute('aria-disabled') !== 'true') { this.state.page++; this.load(true); }
      if (button.hasAttribute('data-filter-toggle')) {
        const open = button.getAttribute('aria-expanded') !== 'true'; button.setAttribute('aria-expanded', String(open)); this.classList.toggle('pl-pr--filters-open', open);
      }
      if (button.hasAttribute('data-strip-step')) this.$('[data-photo-strip]').scrollBy({left: Number(button.dataset.stripStep) * 300, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
      if (button.hasAttribute('data-photo-index')) { this.returnFocus = button; this.showPhoto(Number(button.dataset.photoIndex)); }
      if (button.hasAttribute('data-photo-close')) this.modal.close();
      if (button.hasAttribute('data-photo-step')) this.showPhoto(this.photoIndex + Number(button.dataset.photoStep));
    }
  });
}
